import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, MoreVertical, Check, RotateCcw, Trash2, Printer, Share2, Sparkles, Link2, Unlink, ChevronDown, Users, X, ShoppingCart, ArrowRight } from 'lucide-react';
import { groceries as groceriesApi, hives as hivesApi } from '../utils/api';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { 
  trackGroceryListOpened, 
  trackGroceryItemAdded, 
  trackGroceryItemChecked, 
  trackGroceryListLinkedToHive, 
  trackGroceryListPrinted 
} from '../utils/analytics';

// Aisle display configuration - matches ShoppingListPage styling
const AISLE_CONFIG = {
  produce: { order: 1, label: '🥬 Produce' },
  dairy: { order: 2, label: '🧊 Dairy & Refrigerated' },
  meat: { order: 3, label: '🥩 Meat & Seafood' },
  bakery: { order: 4, label: '🍞 Bakery' },
  pantry: { order: 5, label: '🫙 Pantry' },
  condiments: { order: 6, label: '🍶 Oils & Condiments' },
  spices: { order: 7, label: '🧂 Spices & Seasonings' },
  frozen: { order: 8, label: '❄️ Frozen' },
  beverages: { order: 9, label: '🥤 Beverages' },
  household: { order: 10, label: '🧹 Household' },
  other: { order: 99, label: '📦 Other' },
};

const GroceriesScreen = ({ embedded = false, onBack, onItemsChange, externalItems, headerVisible: externalHeaderVisible, hideChecked: externalHideChecked, onUncheckAll }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('active');
  const [items, setItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showBottomMenu, setShowBottomMenu] = useState(false);
  const [addingItems, setAddingItems] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const inputRef = useRef(null);
  const hasTrackedOpen = useRef(false);
  
  // Hive sync state - initialize from cache for instant UI
  const [hiveStatus, setHiveStatus] = useState(() => {
    try {
      const cached = localStorage.getItem('grocery_hive_status');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      // Ignore parse errors
    }
    return { linked: false, hive_id: null, hive_name: null, member_count: 0 };
  });
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [userHives, setUserHives] = useState([]);
  const [selectedHiveId, setSelectedHiveId] = useState(null);
  const [linking, setLinking] = useState(false);
  
  // History view state
  const [historyView, setHistoryView] = useState('aisle'); // 'aisle' or 'date'
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  
  // Up Next recipes count - check from localStorage cache (for banner)
  const [upNextCount, setUpNextCount] = useState(() => {
    if (embedded) return 0;
    try {
      const cached = localStorage.getItem('recipesCache');
      if (cached) {
        const recipes = JSON.parse(cached);
        return recipes.filter(r => r.up_next && !r.current_meal && !r.deleted).length;
      }
    } catch (e) {
      // Ignore parse errors
    }
    return 0;
  });
  
  // Use external header visibility when embedded
  const effectiveHeaderVisible = embedded ? (externalHeaderVisible ?? true) : headerVisible;
  
  // Toast helper - Active tab shows at top (to not block input), History tab shows at bottom
  const showToast = useCallback((type, message, options = {}) => {
    const position = activeTab === 'history' ? 'bottom-center' : 'top-center';
    toast[type](message, { position, ...options });
  }, [activeTab]);

  // iOS-style scroll behavior - hide header on scroll down, show on scroll up (matches ShoppingListPage)
  useEffect(() => {
    if (embedded) return;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY.current && currentScrollY > 60) {
        setHeaderVisible(false);
      } else {
        setHeaderVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [embedded]);

  // Fetch hive status
  const fetchHiveStatus = useCallback(async () => {
    try {
      const response = await groceriesApi.getStatus();
      const status = response.data;
      setHiveStatus(status);
      // Cache for instant load next time
      localStorage.setItem('grocery_hive_status', JSON.stringify(status));
    } catch (error) {
      console.error('Error fetching hive status:', error);
    }
  }, []);

  // Fetch user's hives for linking
  const fetchUserHives = useCallback(async () => {
    try {
      const response = await hivesApi.getMyHives();
      // API returns all_hives for the My Hives tab, and also dormant hives
      // Include both active and dormant hives for grocery sharing
      const activeHives = response.data.all_hives || [];
      const dormantHives = response.data.dormant || [];
      setUserHives([...activeHives, ...dormantHives]);
    } catch (error) {
      console.error('Error fetching hives:', error);
    }
  }, []);

  // Fetch grocery items
  const fetchGroceries = useCallback(async () => {
    try {
      const response = await groceriesApi.get();
      const newItems = response.data.items || [];
      setItems(newItems);
      
      // Track grocery list opened (only once per session, only for standalone)
      if (!embedded && !hasTrackedOpen.current) {
        hasTrackedOpen.current = true;
        trackGroceryListOpened(newItems.length, hiveStatus.linked);
      }
      
      // Notify parent of item changes for All tab integration
      if (onItemsChange) {
        onItemsChange(newItems);
      }
    } catch (error) {
      console.error('Error fetching groceries:', error);
      toast.error('Failed to load groceries'); // Use toast directly for fetch errors
    } finally {
      setLoading(false);
    }
  }, [onItemsChange, embedded, hiveStatus.linked]);

  // Fetch history items (also dedupe on first load)
  const fetchHistory = useCallback(async () => {
    try {
      // First dedupe any existing duplicates
      await groceriesApi.dedupeHistory();
      const response = await groceriesApi.getHistory();
      setHistoryItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchGroceries();
    fetchHistory();
    fetchHiveStatus();
  }, [fetchGroceries, fetchHistory, fetchHiveStatus]);

  // Check if we should open the share modal (returning from Feed)
  useEffect(() => {
    if (location.state?.openShareModal && !embedded) {
      // Small delay to ensure data is loaded
      setTimeout(() => {
        handleOpenLinkModal();
      }, 300);
      // Clean up state
      window.history.replaceState({}, '', '/groceries');
    }
  }, [location.state, embedded]);

  // Sync on window focus (leave and come back)
  useEffect(() => {
    if (embedded) return;
    
    const handleFocus = () => {
      fetchGroceries();
      fetchHiveStatus();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [embedded, fetchGroceries, fetchHiveStatus]);

  // Reset input focus state when component mounts or when navigating back to it
  // This fixes the issue where bottom nav disappears after navigating between pages
  useEffect(() => {
    setIsInputFocused(false);
  }, []);

  // Notify parent whenever items change
  useEffect(() => {
    if (onItemsChange && items.length >= 0) {
      onItemsChange(items);
    }
  }, [items, onItemsChange]);

  // Handle uncheck all from parent (ShoppingListPage)
  const uncheckAllGroceries = useCallback(async () => {
    const checkedItems = items.filter(i => i.checked);
    if (checkedItems.length === 0) return;
    
    // Optimistic update
    setItems(prev => prev.map(item => ({ ...item, checked: false })));
    
    try {
      await Promise.all(
        checkedItems.map(item => groceriesApi.updateItem(item.id, { checked: false }))
      );
    } catch (error) {
      // Revert on error - refetch
      fetchGroceries();
      toast.error('Failed to uncheck items');
    }
  }, [items, fetchGroceries]);

  // Expose uncheckAll via callback when it changes
  useEffect(() => {
    if (onUncheckAll) {
      onUncheckAll(uncheckAllGroceries);
    }
  }, [onUncheckAll, uncheckAllGroceries]);

  // Group items by aisle and sort alphabetically
  const groupByAisle = (itemsList) => {
    const grouped = {};
    itemsList.forEach(item => {
      const aisle = item.aisle || 'other';
      if (!grouped[aisle]) {
        grouped[aisle] = [];
      }
      grouped[aisle].push(item);
    });

    // Sort aisles by order, and items within each aisle alphabetically
    return Object.entries(grouped)
      .sort(([a], [b]) => {
        const orderA = AISLE_CONFIG[a]?.order || 99;
        const orderB = AISLE_CONFIG[b]?.order || 99;
        return orderA - orderB;
      })
      .map(([aisle, items]) => [
        aisle,
        items.sort((a, b) => a.name.localeCompare(b.name))
      ]);
  };

  // Add items
  const handleAddItems = async () => {
    if (!inputValue.trim() || addingItems) return;

    setAddingItems(true);
    try {
      // Parse input - split by comma, "and", or newline
      const rawItems = inputValue
        .split(/,|\band\b|\n/)
        .map(s => s.trim().toLowerCase())
        .filter(s => s && s.length > 0);

      // Clean up each item individually - remove shopping phrases
      const SHOPPING_PHRASES = [
        /^(i want to |i need to |i'd like to |i would like |we need |please |can you |could you )/i,
        /^(add |buy |shop for |get |pick up |grab |need |want )/i,
        /^(some |a few |a couple |the |more )/i,
        /\s*(please|thanks|thank you)$/i,
      ];
      
      const itemNames = rawItems
        .map(item => {
          let cleaned = item;
          // Apply all cleaning patterns
          for (const pattern of SHOPPING_PHRASES) {
            cleaned = cleaned.replace(pattern, '');
          }
          return cleaned.trim();
        })
        .filter(s => s && s.length > 1 && s.length < 100); // Filter empty, single-char, and too-long strings

      if (itemNames.length === 0) {
        showToast('error', 'Please enter valid grocery items');
        setAddingItems(false);
        return;
      }

      const response = await groceriesApi.add(itemNames);
      
      if (response.data.added?.length > 0) {
        setItems(prev => [...prev, ...response.data.added]);
        toast.success(`Added ${response.data.added.length} item${response.data.added.length > 1 ? 's' : ''}`);
        // Dispatch event so badge updates
        window.dispatchEvent(new CustomEvent('groceries-changed', { detail: { fromEmbedded: embedded } }));
        
        // Track each item added
        response.data.added.forEach(item => {
          trackGroceryItemAdded(item.name, hiveStatus.linked);
        });
      }
      
      if (response.data.duplicates?.length > 0) {
        toast.info(`${response.data.duplicates.join(', ')} already on your list`);
      }

      setInputValue('');
    } catch (error) {
      console.error('Error adding items:', error);
      showToast('error', 'Failed to add items');
    } finally {
      setAddingItems(false);
    }
  };

  // Toggle item checked state
  const handleToggleItem = async (itemId, currentChecked) => {
    try {
      await groceriesApi.updateItem(itemId, { checked: !currentChecked });
      const toggledItem = items.find(item => item.id === itemId);
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, checked: !currentChecked } : item
      ));
      
      // Track item checked (only when checking, not unchecking)
      if (!currentChecked && toggledItem) {
        trackGroceryItemChecked(toggledItem.name, hiveStatus.linked);
      }
    } catch (error) {
      console.error('Error toggling item:', error);
      showToast('error', 'Failed to update item');
    }
  };

  // Menu actions
  const handleCheckAll = async () => {
    setShowBottomMenu(false);
    console.log('handleCheckAll called');
    try {
      console.log('Calling groceriesApi.checkAll()');
      await groceriesApi.checkAll();
      console.log('checkAll API returned');
      setItems(prev => prev.map(item => ({ ...item, checked: true })));
      showToast('success', 'All items checked');
    } catch (error) {
      console.error('checkAll error:', error);
      showToast('error', 'Failed to check all items');
    }
  };

  const handleUncheckAll = async () => {
    setShowBottomMenu(false);
    try {
      await groceriesApi.uncheckAll();
      setItems(prev => prev.map(item => ({ ...item, checked: false })));
      showToast('success', 'All items unchecked');
    } catch (error) {
      showToast('error', 'Failed to uncheck all items');
    }
  };

  const handleReclassify = async () => {
    setShowBottomMenu(false);
    if (items.length === 0) {
      showToast('info', 'No items to reclassify');
      return;
    }

    try {
      await groceriesApi.reclassify();
      showToast('success', 'Fixing aisles... refresh in a few seconds');
      // Refetch after a delay to get updated aisles
      setTimeout(async () => {
        try {
          const response = await groceriesApi.getAll();
          setItems(response.data.items || []);
        } catch (e) {
          // Ignore errors on refresh
        }
      }, 3000);
    } catch (error) {
      showToast('error', 'Failed to reclassify items');
    }
  };

  const handleClearChecked = async () => {
    setShowBottomMenu(false);
    const checkedCount = items.filter(i => i.checked).length;
    console.log('handleClearChecked called, checkedCount:', checkedCount);
    if (checkedCount === 0) {
      showToast('info', 'No checked items to clear');
      return;
    }

    try {
      console.log('Calling groceriesApi.clearChecked()');
      await groceriesApi.clearChecked();
      console.log('clearChecked API returned');
      setItems(prev => prev.filter(item => !item.checked));
      // Refetch history to get correct IDs from backend
      await fetchHistory();
      toast.success(`Cleared ${checkedCount} item${checkedCount > 1 ? 's' : ''}`);
      // Dispatch event so badge updates
      window.dispatchEvent(new CustomEvent('groceries-changed', { detail: { fromEmbedded: embedded } }));
    } catch (error) {
      console.error('clearChecked error:', error);
      showToast('error', 'Failed to clear checked items');
    }
  };

  const handleStartFresh = async () => {
    setShowBottomMenu(false);
    if (items.length === 0) {
      showToast('info', 'No items to clear');
      return;
    }

    if (!window.confirm('Clear all grocery items? They will be saved in History.')) {
      return;
    }

    try {
      await groceriesApi.clearAll();
      setItems([]);
      // Refetch history to get correct IDs from backend
      await fetchHistory();
      showToast('success', 'Grocery list cleared');
      // Dispatch event so badge updates
      window.dispatchEvent(new CustomEvent('groceries-changed', { detail: { fromEmbedded: embedded } }));
    } catch (error) {
      showToast('error', 'Failed to clear list');
    }
  };

  // History actions
  const handleRestoreFromHistory = async (historyItem) => {
    try {
      const response = await groceriesApi.restoreFromHistory(historyItem.id);
      if (response.data.success) {
        setItems(prev => [...prev, response.data.item]);
        showToast('success', `${historyItem.name} added to list`);
        // Dispatch event so badge updates
        window.dispatchEvent(new CustomEvent('groceries-changed', { detail: { fromEmbedded: embedded } }));
      } else {
        showToast('info', response.data.message || 'Item already on your list');
      }
    } catch (error) {
      showToast('error', 'Failed to restore item');
    }
  };

  const handleDeleteFromHistory = async (historyItem) => {
    try {
      await groceriesApi.deleteFromHistory(historyItem.id);
      setHistoryItems(prev => prev.filter(i => i.id !== historyItem.id));
      // Show undo toast with actual restore functionality - always bottom for history
      toast.success(`Deleted "${historyItem.name}"`, {
        position: 'bottom-center',
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const response = await groceriesApi.addToHistory({
                name: historyItem.name,
                aisle: historyItem.aisle
              });
              if (response.data.success) {
                setHistoryItems(prev => [response.data.item, ...prev]);
                showToast('success', 'Restored to history');
              }
            } catch (e) {
              showToast('error', 'Failed to undo');
            }
          }
        }
      });
    } catch (error) {
      showToast('error', 'Failed to delete item');
    }
  };

  // Hive link/unlink handlers
  const handleOpenLinkModal = async () => {
    setShowHeaderMenu(false);
    await fetchUserHives();
    setSelectedHiveId(null);
    setShowLinkModal(true);
  };

  const handleLinkToHive = async () => {
    if (!selectedHiveId || linking) return;
    
    setLinking(true);
    try {
      const response = await groceriesApi.linkToHive(selectedHiveId);
      if (response.data.success) {
        toast.success(`Linked to ${response.data.hive_name}! ${response.data.items_merged} items merged.`);
        setShowLinkModal(false);
        
        // Track linking to hive
        trackGroceryListLinkedToHive(selectedHiveId);
        
        // Refresh data
        await fetchHiveStatus();
        await fetchGroceries();
        await fetchHistory();
      }
    } catch (error) {
      console.error('Error linking to hive:', error);
      toast.error(error.response?.data?.detail || 'Failed to link to hive');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkFromHive = async () => {
    setLinking(true);
    try {
      await groceriesApi.unlinkFromHive();
      toast.success('Switched to personal grocery list');
      setShowUnlinkModal(false);
      // Clear cached status
      localStorage.removeItem('grocery_hive_status');
      // Refresh data
      await fetchHiveStatus();
      await fetchGroceries();
      await fetchHistory();
    } catch (error) {
      console.error('Error unlinking from hive:', error);
      toast.error('Failed to unlink from hive');
    } finally {
      setLinking(false);
    }
  };

  // Group history by date for "By Date" view
  const groupHistoryByDate = (historyList) => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    historyList.forEach(item => {
      // Determine the date and type (checked vs cleared)
      const isChecked = !!item.checked_at;
      const eventDate = new Date(item.checked_at || item.cleared_at);
      const dateString = eventDate.toDateString();
      
      let dateLabel;
      if (dateString === today) {
        dateLabel = 'Today';
      } else if (dateString === yesterday) {
        dateLabel = 'Yesterday';
      } else {
        dateLabel = eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      
      // Get attribution (who checked or who cleared)
      const attributedTo = item.checked_by?.display_name || item.cleared_by?.display_name;
      
      // Create group key: "Checked|Today|John D." or "Cleared|Today|Jane D."
      const actionType = isChecked ? 'Checked' : 'Cleared';
      const groupKey = attributedTo 
        ? `${actionType}|${dateLabel}|${attributedTo}` 
        : `${actionType}|${dateLabel}`;
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          actionType,
          dateLabel,
          attributedTo,
          items: [],
          sortDate: eventDate
        };
      }
      groups[groupKey].items.push(item);
    });
    
    // Sort by date (most recent first)
    return Object.values(groups).sort((a, b) => b.sortDate - a.sortDate);
  };

  // Print grocery list
  const handlePrint = () => {
    setShowHeaderMenu(false);
    
    // Track print action
    trackGroceryListPrinted(items.length, hiveStatus.linked);
    
    const printWindow = window.open('', '_blank');
    const groupedItems = groupByAisle(items);
    
    // Build grocery items HTML
    let groceryHtml = '';
    groupedItems.forEach(([aisle, aisleItems]) => {
      const label = AISLE_CONFIG[aisle]?.label || '📦 Other';
      const itemsHtml = aisleItems.map(item => 
        `<div class="item${item.checked ? ' checked' : ''}" onclick="this.classList.toggle('checked')">
          <div class="checkbox">${item.checked ? '✓' : ''}</div>
          <span>${item.name}</span>
        </div>`
      ).join('');
      groceryHtml += `<div class="aisle">
        <div class="aisle-header">${label}</div>
        ${itemsHtml}
      </div>`;
    });
    
    const html = `<!DOCTYPE html>
<html>
<head>
  <title>My Groceries</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; max-width: 600px; margin: 0 auto; }
    h1 { font-size: 24px; margin-bottom: 8px; }
    .instructions { background: #FEF3C7; padding: 12px; border-radius: 8px; margin-bottom: 20px; font-size: 14px; }
    .aisle { margin-bottom: 16px; }
    .aisle-header { font-weight: 600; font-size: 16px; margin-bottom: 8px; color: #374151; }
    .item { display: flex; align-items: center; padding: 6px 0; cursor: pointer; }
    .item.checked { text-decoration: line-through; color: #9CA3AF; background: #FEF9C3; }
    .checkbox { width: 18px; height: 18px; border: 2px solid #D1D5DB; border-radius: 50%; margin-right: 10px; display: flex; align-items: center; justify-content: center; }
    .item.checked .checkbox { background: #d97706; border-color: #d97706; color: white; }
    .return-btn { 
      position: fixed; 
      bottom: 20px; 
      right: 20px; 
      background: linear-gradient(135deg, #f59e0b, #d97706); 
      color: white; 
      border: none; 
      padding: 12px 20px; 
      border-radius: 50px; 
      font-size: 14px; 
      font-weight: 600; 
      cursor: pointer; 
      box-shadow: 0 4px 15px rgba(217, 119, 6, 0.4); 
      display: flex; 
      align-items: center; 
      gap: 8px;
      transition: transform 0.2s, box-shadow 0.2s;
      z-index: 9999;
    }
    .return-btn:hover { 
      transform: translateY(-2px); 
      box-shadow: 0 6px 20px rgba(217, 119, 6, 0.5); 
    }
    @media print { 
      .instructions { display: none; } 
      .item.checked { display: none; }
      .return-btn { display: none; }
    }
  </style>
</head>
<body>
  <h1>🛒 My Groceries</h1>
  <div class="instructions">
    <strong>Interactive!</strong> Check items you already have to strike them through. Then press Ctrl+P (or ⌘+P on Mac) to print. Checked items will be hidden from the printout.
  </div>
  ${groceryHtml}
  <button class="return-btn" onclick="window.close();">
    🍯 Return to Honey
  </button>
</body>
</html>`;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Share grocery list
  const handleShare = async () => {
    setShowHeaderMenu(false);
    const groupedItems = groupByAisle(items.filter(i => !i.checked));
    
    let text = '🛒 My Groceries\n\n';
    groupedItems.forEach(([aisle, aisleItems]) => {
      const label = AISLE_CONFIG[aisle]?.label || '📦 Other';
      text += `${label}\n`;
      aisleItems.forEach(item => {
        text += `• ${item.name}\n`;
      });
      text += '\n';
    });

    if (navigator.share) {
      try {
        await navigator.share({ title: 'My Groceries', text });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to clipboard
          await navigator.clipboard.writeText(text);
          showToast('success', 'Copied to clipboard');
        }
      }
    } else {
      await navigator.clipboard.writeText(text);
      showToast('success', 'Copied to clipboard');
    }
  };

  // When embedded with hideChecked, filter out checked items from display
  const displayItems = embedded && externalHideChecked 
    ? items.filter(i => !i.checked) 
    : items;
  const groupedActiveItems = groupByAisle(displayItems);
  // For By Aisle view, filter out deleted items (they stay in By Date timeline)
  const restorableHistoryItems = historyItems.filter(i => !i.deleted);
  const groupedHistoryItems = groupByAisle(restorableHistoryItems);
  const checkedCount = items.filter(i => i.checked).length;

  return (
    <div className={`bg-amber-50 ${embedded ? '' : 'min-h-screen pb-20'}`}>
      {/* iOS-style collapsing header - matches ShoppingListPage */}
      {!embedded && (
        <header 
          className={`bg-white/80 backdrop-blur-sm shadow-sm fixed top-0 left-0 right-0 z-40 transition-transform duration-300 ${
            effectiveHeaderVisible ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-900">My Groceries</h1>
                <p className="text-sm text-gray-500">{items.length} item{items.length !== 1 ? 's' : ''}{checkedCount > 0 ? ` • ${checkedCount} checked` : ''}</p>
              </div>
              <div className="relative">
                <button
                  onClick={() => {
                    setShowBottomMenu(false);
                    setShowHeaderMenu(!showHeaderMenu);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  data-testid="groceries-header-menu-btn"
                >
                  <MoreVertical size={20} />
                </button>
                {showHeaderMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowHeaderMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border z-50 py-1 min-w-[180px]">
                      <button
                        onClick={handlePrint}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        data-testid="groceries-print-btn"
                      >
                        <Printer size={16} />
                        Print
                      </button>
                      <button
                        onClick={handleShare}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        data-testid="groceries-share-btn"
                      >
                        <Share2 size={16} />
                        Share
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Spacer for fixed header */}
      {!embedded && <div className="h-[72px]" />}

      {/* Underlined tabs for Active/History */}
      <div className={`sticky z-20 bg-white/95 backdrop-blur-sm border-b transition-all duration-300 ${
        embedded 
          ? (effectiveHeaderVisible ? 'top-[161px]' : 'top-[89px]')
          : (effectiveHeaderVisible ? 'top-[72px]' : 'top-0')
      }`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-6 items-center justify-between">
            <div className="flex gap-6">
              <button
                onClick={() => setActiveTab('active')}
                data-testid="groceries-active-tab"
                className={`py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === 'active' 
                    ? 'border-amber-600 text-amber-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Active {items.length > 0 && <span className="opacity-75">({items.length})</span>}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                data-testid="groceries-history-tab"
                className={`py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === 'history' 
                    ? 'border-amber-600 text-amber-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                History {historyItems.length > 0 && <span className="opacity-75">({historyItems.length})</span>}
              </button>
            </div>
            
            {/* History view dropdown */}
            {activeTab === 'history' && (
              <div className="relative">
                <button
                  onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 py-2"
                  data-testid="history-view-dropdown"
                >
                  {historyView === 'aisle' ? 'By Aisle' : 'By Date'}
                  <ChevronDown size={16} />
                </button>
                {showHistoryDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border py-1 z-30 min-w-[120px]">
                    <button
                      onClick={() => { setHistoryView('aisle'); setShowHistoryDropdown(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${historyView === 'aisle' ? 'text-amber-600 font-medium' : ''}`}
                    >
                      By Aisle
                    </button>
                    <button
                      onClick={() => { setHistoryView('date'); setShowHistoryDropdown(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${historyView === 'date' ? 'text-amber-600 font-medium' : ''}`}
                    >
                      By Date
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hive Link Banner */}
      {!embedded && (
        <div className="max-w-4xl mx-auto px-4 pt-3">
          {hiveStatus.linked ? (
            <div 
              className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between"
              data-testid="hive-linked-banner"
            >
              <div className="flex items-center gap-2">
                <Users size={18} className="text-emerald-600" />
                <div>
                  <span className="font-medium text-emerald-800">{hiveStatus.hive_name}</span>
                  <span className="text-emerald-600 text-sm ml-2">
                    Shared · {hiveStatus.member_count} member{hiveStatus.member_count !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowUnlinkModal(true)}
                className="text-emerald-600 hover:text-emerald-800 text-sm font-medium flex items-center gap-1"
                data-testid="unlink-hive-btn"
              >
                <Unlink size={14} />
                Unlink
              </button>
            </div>
          ) : (
            <button
              onClick={handleOpenLinkModal}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
              data-testid="link-hive-btn"
            >
              <div className="flex items-center gap-2 text-gray-600">
                <span className="text-lg">📋</span>
                <span>Personal List</span>
              </div>
              <span className="text-amber-600 text-sm font-medium flex items-center gap-1">
                Share with a Hive
                <Link2 size={14} />
              </span>
            </button>
          )}
          
          {/* Up Next Shopping List Banner - shows when user has Up Next recipes and hasn't clicked through yet */}
          {upNextCount > 0 && !localStorage.getItem('hasUsedUpNextShoppingBanner') && (
            <button
              onClick={() => {
                localStorage.setItem('hasUsedUpNextShoppingBanner', 'true');
                navigate('/recipe-box', { state: { scrollToUpNext: true, showShoppingTip: true } });
              }}
              className="w-full mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
              data-testid="up-next-shopping-banner"
            >
              <div className="flex items-center gap-2 text-blue-700">
                <ShoppingCart size={18} />
                <span className="text-sm">Looking for recipe ingredients?</span>
              </div>
              <span className="text-blue-600 text-sm font-medium flex items-center gap-1">
                Up Next Shopping List
                <ArrowRight size={14} />
              </span>
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-4 pb-32">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
          </div>
        ) : activeTab === 'active' ? (
          /* Active Tab */
          items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">No groceries yet</p>
              <p className="text-sm">Add items below to get started</p>
            </div>
          ) : (
            <div className="space-y-6">
              {groupedActiveItems.map(([aisle, aisleItems]) => (
                <div key={aisle}>
                  {/* Sticky aisle header - matches mobile with gray background strip */}
                  <h3 className={`text-base font-semibold text-gray-800 mb-2 sticky bg-gray-100/80 backdrop-blur-sm rounded-md py-2 px-3 z-10 transition-all duration-300 ${
                    embedded 
                      ? (effectiveHeaderVisible ? 'top-[168px]' : 'top-[96px]')
                      : (effectiveHeaderVisible ? 'top-[120px]' : 'top-[48px]')
                  }`}>
                    {AISLE_CONFIG[aisle]?.label || '📦 Other'}
                  </h3>
                  <div className="space-y-0.5">
                    {aisleItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => handleToggleItem(item.id, item.checked)}
                        data-testid={`grocery-item-${item.id}`}
                        className="w-full flex items-start gap-3 py-2.5 px-3 rounded-lg hover:bg-white/50 transition-colors text-left group"
                      >
                        {/* Checkbox matching mobile style */}
                        <div className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors mt-0.5 ${
                          item.checked 
                            ? 'bg-amber-600 border-amber-600' 
                            : 'border-gray-300 group-hover:border-amber-400'
                        }`}>
                          {item.checked && <Check size={14} className="text-white" />}
                        </div>
                        <div className="flex-1">
                          <span className={`block ${item.checked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                            {item.name}
                          </span>
                          {hiveStatus.linked && item.added_by && (
                            <span className="text-xs text-gray-400">
                              {item.added_by.display_name}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* History Tab */
          historyItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">No history yet</p>
              <p className="text-sm">Cleared items will appear here</p>
            </div>
          ) : historyView === 'aisle' ? (
            /* By Aisle View */
            <div className="space-y-6">
              {groupedHistoryItems.map(([aisle, aisleItems]) => (
                <div key={aisle}>
                  <h3 className={`text-base font-semibold text-gray-800 mb-2 sticky bg-gray-100/80 backdrop-blur-sm rounded-md py-2 px-3 z-10 transition-all duration-300 ${
                    embedded 
                      ? (effectiveHeaderVisible ? 'top-[168px]' : 'top-[96px]')
                      : (effectiveHeaderVisible ? 'top-[120px]' : 'top-[48px]')
                  }`}>
                    {AISLE_CONFIG[aisle]?.label || '📦 Other'}
                  </h3>
                  <div className="space-y-0.5">
                    {aisleItems.map(item => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/50 transition-colors"
                      >
                        <span className="text-gray-700">{item.name}</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleRestoreFromHistory(item)}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-full transition-colors"
                            title="Add to list"
                            data-testid={`restore-${item.id}`}
                          >
                            <Plus size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteFromHistory(item)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                            title="Delete permanently"
                            data-testid={`delete-${item.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* By Date View - shows ALL items including deleted (permanent timeline) */
            <div className="space-y-6">
              {groupHistoryByDate(historyItems).map((group, idx) => (
                <div key={idx}>
                  <h3 className={`text-base font-semibold text-gray-800 mb-2 sticky bg-gray-100/80 backdrop-blur-sm rounded-md py-2 px-3 z-10 transition-all duration-300 ${
                    embedded 
                      ? (effectiveHeaderVisible ? 'top-[168px]' : 'top-[96px]')
                      : (effectiveHeaderVisible ? 'top-[120px]' : 'top-[48px]')
                  }`}>
                    {group.actionType} {group.dateLabel}
                    {group.attributedTo && (
                      <span className="font-normal text-gray-500 ml-2">by {group.attributedTo}</span>
                    )}
                  </h3>
                  <div className="space-y-0.5 pl-4 border-l-2 border-gray-200">
                    {group.items.map(item => (
                      <div
                        key={item.id}
                        className={`flex items-center py-1.5 ${item.deleted ? 'text-gray-400 line-through' : 'text-gray-600'}`}
                      >
                        <span>├─ {item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Bottom Input Bar (Active tab only) - hidden when input is focused on iOS to avoid keyboard overlap */}
      {activeTab === 'active' && !isInputFocused && (
        <>
          {/* Backdrop overlay for menu - must be at root level */}
          {showBottomMenu && (
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowBottomMenu(false)} 
            />
          )}
          {/* Menu popup - rendered at root level to escape z-index stacking */}
          {showBottomMenu && (
            <div className={`fixed right-4 bg-white rounded-lg shadow-lg border z-50 py-1 min-w-[180px] ${
              embedded ? 'bottom-16' : 'bottom-32'
            }`}>
              <button
                onClick={handleCheckAll}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3"
                data-testid="check-all-btn"
              >
                <Check size={16} className="text-amber-600" />
                Check All
              </button>
              <button
                onClick={handleUncheckAll}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3"
                data-testid="uncheck-all-btn"
              >
                <RotateCcw size={16} className="text-amber-600" />
                Uncheck All
              </button>
              <button
                onClick={handleClearChecked}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3"
                data-testid="clear-checked-btn"
              >
                <Trash2 size={16} className="text-amber-600" />
                Clear Checked {checkedCount > 0 && `(${checkedCount})`}
              </button>
              <button
                onClick={handleReclassify}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3"
                data-testid="reclassify-btn"
              >
                <Sparkles size={16} className="text-amber-600" />
                Fix Aisles
              </button>
              <hr className="my-1" />
              <button
                onClick={handleStartFresh}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 flex items-center gap-3 text-red-600"
                data-testid="start-fresh-btn"
              >
                <Trash2 size={16} />
                Start Fresh
              </button>
            </div>
          )}
          <div className={`fixed left-0 right-0 bg-white/95 backdrop-blur-sm border-t shadow-lg z-30 ${
            embedded ? 'bottom-0' : 'bottom-16'
          }`}>
            <div className="max-w-4xl mx-auto px-4 py-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">Add Grocery</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddItems()}
                  onFocus={() => setIsInputFocused(true)}
                  onBlur={() => setTimeout(() => setIsInputFocused(false), 100)}
                  placeholder="milk, bread, eggs..."
                  autoCapitalize="none"
                  data-testid="grocery-input"
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <button
                  onClick={() => {
                    setShowHeaderMenu(false);
                    setShowBottomMenu(!showBottomMenu);
                  }}
                  className="p-2.5 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
                  data-testid="groceries-bottom-menu-btn"
                >
                  <MoreVertical size={20} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Floating input when focused - appears above keyboard */}
      {activeTab === 'active' && isInputFocused && (
        <div 
          className="fixed left-0 right-0 bottom-0 bg-white border-t shadow-lg z-50"
        >
          <div className="max-w-4xl mx-auto px-4 py-3 pb-safe">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddItems();
                    e.target.blur();
                  }
                }}
                onBlur={() => setTimeout(() => setIsInputFocused(false), 150)}
                placeholder="milk, bread, eggs..."
                autoCapitalize="none"
                autoFocus
                className="flex-1 px-4 py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }} /* Prevents iOS zoom on focus */
              />
              <button
                onClick={() => {
                  handleAddItems();
                  setIsInputFocused(false);
                }}
                data-testid="grocery-add-btn"
                className="px-5 py-3 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors font-medium"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Nav - hidden when input is focused to make room for keyboard */}
      {!embedded && !isInputFocused && <BottomNav />}

      {/* Link to Hive Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center pb-16 sm:pb-0">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[75vh] sm:max-h-[85vh] overflow-hidden shadow-xl flex flex-col">
            {/* Fixed header */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Link2 size={20} />
                  <h2 className="text-lg font-bold">Share Grocery List</h2>
                </div>
                <button 
                  onClick={() => setShowLinkModal(false)}
                  className="text-white/80 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 min-h-0">
              <p className="text-gray-600 p-4 pb-2 text-sm">
                Share your grocery list with a Hive so everyone can add items and check things off together.
              </p>
              
              {userHives.length === 0 ? (
                <div className="text-center py-8 px-4 text-gray-500">
                  <p>You're not a member of any Hives yet.</p>
                  <button
                    onClick={() => { setShowLinkModal(false); navigate('/feed'); }}
                    className="mt-2 text-amber-600 font-medium"
                  >
                    Create or join a Hive →
                  </button>
                </div>
              ) : (
                <div className="px-4 pb-4">
                  {/* Active hives */}
                  {userHives.filter(h => !h.is_dormant).length > 0 && (
                    <div className="space-y-2 mb-4">
                      {userHives.filter(h => !h.is_dormant).map(hive => (
                        <label
                          key={hive.hive_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedHiveId === hive.hive_id 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="hive"
                            value={hive.hive_id}
                            checked={selectedHiveId === hive.hive_id}
                            onChange={() => setSelectedHiveId(hive.hive_id)}
                            className="text-emerald-500"
                          />
                          <span className="text-xl">{hive.icon || '🐝'}</span>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium block truncate">{hive.name}</span>
                            <span className="text-gray-500 text-xs">
                              {hive.member_count || 1} member{(hive.member_count || 1) !== 1 ? 's' : ''}
                              {hive.hive_type === 'private' && ' · 🔒'}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {/* Dormant hives section */}
                  {userHives.filter(h => h.is_dormant).length > 0 && (
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                        <span>💤</span> Dormant Hives
                      </p>
                      <div className="space-y-2">
                        {userHives.filter(h => h.is_dormant).map(hive => (
                          <label
                            key={hive.hive_id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedHiveId === hive.hive_id 
                                ? 'border-emerald-500 bg-emerald-50' 
                                : 'border-gray-100 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="hive"
                              value={hive.hive_id}
                              checked={selectedHiveId === hive.hive_id}
                              onChange={() => setSelectedHiveId(hive.hive_id)}
                              className="text-emerald-500"
                            />
                            <span className="text-xl opacity-50">{hive.icon || '🐝'}</span>
                            <div className="flex-1 min-w-0">
                              <span className="font-medium block truncate text-gray-500">{hive.name}</span>
                              <span className="text-gray-400 text-xs">
                                {hive.member_count || 1} member{(hive.member_count || 1) !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Find more hives option */}
                  <button 
                    onClick={() => { 
                      setShowLinkModal(false); 
                      navigate('/feed?tab=discover', { 
                        state: { returnToGroceryShare: true } 
                      }); 
                    }}
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-emerald-50 cursor-pointer w-full text-left group mt-3 border border-dashed border-gray-200"
                  >
                    <span className="text-emerald-500">+</span>
                    <span className="text-xl">🔍</span>
                    <span className="text-sm font-medium text-emerald-600 group-hover:underline">Find more hives</span>
                  </button>
                </div>
              )}
            </div>
            
            {/* Fixed footer */}
            {userHives.length > 0 && (
              <div className="border-t p-4 flex-shrink-0 bg-white">
                {items.length > 0 && (
                  <p className="text-xs text-gray-500 mb-3 text-center">
                    Your {items.length} item{items.length !== 1 ? 's' : ''} will be added to the shared list.
                  </p>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLinkToHive}
                    disabled={!selectedHiveId || linking}
                    className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    data-testid="confirm-link-btn"
                  >
                    {linking ? 'Linking...' : 'Share with Hive'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Unlink from Hive Modal */}
      {showUnlinkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Unlink from Hive?</h2>
              <button 
                onClick={() => setShowUnlinkModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Your grocery list will become personal again.
            </p>
            <p className="text-gray-600 mb-6">
              Items you added will stay with "{hiveStatus.hive_name}" shared list.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowUnlinkModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUnlinkFromHive}
                disabled={linking}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                data-testid="confirm-unlink-btn"
              >
                {linking ? 'Unlinking...' : 'Unlink'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroceriesScreen;
